import {
  getSavedRunArtifacts,
  getSavedRunOfficialSummary,
  getSavedRunExecutionSetup,
  getSavedRunIdentity,
  getSavedRunLineageSummary,
  getSavedRunMeasurementInputs,
  getSavedRunParams,
  getSavedRunRequestedTraces,
  getSavedRunEffectiveTraces,
  getSavedRunProjectSample,
  getSavedRunSampleLabel,
  getSavedRunSourceRunId,
} from './savedRunDetail';

const PARAM_CHANGE = 'PARAM_CHANGE';
const TRACE_CHANGE = 'TRACE_CHANGE';
const CONFIRMATION = 'CONFIRMATION';

export const DERIVED_PREVIEW_STATUS = Object.freeze({
  IDLE: 'idle',
  LOADING: 'loading',
  VALID: 'valid',
  STALE: 'stale',
  ERROR: 'error',
});

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function cloneJsonValue(value) {
  if (value === undefined) {
    return undefined;
  }
  return JSON.parse(JSON.stringify(value));
}

export function getRunProvenanceSummary(run) {
  return isPlainObject(run?.provenance_summary) ? run.provenance_summary : null;
}

export function buildDerivedSeedFromSavedRunDetail(runDetail) {
  const identity = getSavedRunIdentity(runDetail);
  if (!isPlainObject(identity) || !identity.run_id || !identity.pipeline) {
    return null;
  }

  const params = getSavedRunParams(runDetail);
  const measurementInputs = getSavedRunMeasurementInputs(runDetail);

  return {
    identity: cloneJsonValue(identity),
    project_sample: cloneJsonValue(getSavedRunProjectSample(runDetail)),
    lineage_summary: cloneJsonValue(getSavedRunLineageSummary(runDetail)),
    frozen_inputs: {
      params: params == null ? null : cloneJsonValue(params),
    },
    measurement_inputs: cloneJsonValue(measurementInputs),
    execution_setup: cloneJsonValue(getSavedRunExecutionSetup(runDetail)),
    source_expected_t0: getSavedRunOfficialSummary(runDetail)?.components?.[0]?.validity?.inputs?.t0_predicted
      ?? getSavedRunOfficialSummary(runDetail)?.components?.[0]?.validity?.result?.t0_predicted ?? null,
    source_taylorgram: cloneJsonValue(getSavedRunArtifacts(runDetail)?.size_plots?.taylorgram || null),
  };
}

function getDerivedSeedIdentity(seed) {
  return isPlainObject(seed?.identity) ? seed.identity : null;
}

export function getDerivedSeedRunId(seed) {
  return getDerivedSeedIdentity(seed)?.run_id || null;
}

export function getDerivedSeedPipeline(seed) {
  return getDerivedSeedIdentity(seed)?.pipeline || null;
}

export function getDerivedSeedStartedAt(seed) {
  const identity = getDerivedSeedIdentity(seed);
  return identity?.started_at || identity?.created_at || identity?.finished_at || null;
}

export function getDerivedSeedSampleLabel(seed) {
  return getSavedRunSampleLabel(seed);
}

export function getDerivedSeedSourceRunId(seed) {
  return getSavedRunSourceRunId(seed);
}

export function getDerivedSeedParams(seed) {
  return getSavedRunParams(seed);
}

export function getDerivedSeedRequestedTraces(seed) {
  return getSavedRunRequestedTraces(seed);
}

export function getDerivedSeedEffectiveTraces(seed) {
  return getSavedRunEffectiveTraces(seed);
}

export function getDerivedSeedExecutionSetupDetail(seed) {
  return getSavedRunExecutionSetup(seed);
}

export function getRunParamsFromProvenance(run) {
  const params = getRunProvenanceSummary(run)?.params;
  return isPlainObject(params) ? cloneJsonValue(params) : null;
}

function getRunInputSnapshots(run) {
  const inputs = getRunProvenanceSummary(run)?.inputs;
  return Array.isArray(inputs) ? inputs.filter(isPlainObject) : [];
}

function normalizeTraceSelector(selector) {
  const traceId =
    selector?.trace_id === '' || selector?.trace_id == null
      ? null
      : String(selector.trace_id).trim();
  const rawWavelength = selector?.wavelength_nm;

  if (rawWavelength === '' || rawWavelength == null) {
    return { trace_id: traceId, wavelength_nm: null };
  }

  const wavelength = Number(rawWavelength);
  return {
    trace_id: traceId,
    wavelength_nm: Number.isFinite(wavelength) ? wavelength : rawWavelength,
  };
}

export function getRunRequestedTraces(run) {
  return getRunInputSnapshots(run).map((input) => {
    const requestedTrace = normalizeTraceSelector(input.requested_trace || {});
    return {
      import_id: input.import_id,
      trace_id: requestedTrace.trace_id,
      wavelength_nm: requestedTrace.wavelength_nm,
      filename: input?.file?.original_filename || null,
      file_role: input?.file_role || null,
    };
  });
}

export function getRunEffectiveTraces(run) {
  return getRunInputSnapshots(run).map((input) => {
    const effectiveTrace = normalizeTraceSelector(input.effective_trace || {});
    return {
      import_id: input.import_id,
      trace_id: effectiveTrace.trace_id,
      wavelength_nm: effectiveTrace.wavelength_nm,
      time_unit_raw: input?.effective_trace?.time_unit_raw || null,
      signal_unit_raw: input?.effective_trace?.signal_unit_raw || null,
      signal_unit_family: input?.effective_trace?.signal_unit_family || null,
      signal_unit_canonical: input?.effective_trace?.signal_unit_canonical || null,
      source_format: input?.effective_trace?.source_format || null,
      filename: input?.file?.original_filename || null,
      file_role: input?.file_role || null,
    };
  });
}

export function getRunSourceRunId(run) {
  return run?.lineage_summary?.source_run_id || run?.rerun_of || null;
}

function normalizeFrozenSetupSnapshot(setupSnapshot) {
  if (!isPlainObject(setupSnapshot)) {
    return null;
  }

  const values = isPlainObject(setupSnapshot.values) ? setupSnapshot.values : {};
  const resolvedMetadata = isPlainObject(setupSnapshot.resolved_size_metadata)
    ? setupSnapshot.resolved_size_metadata
    : {};
  const pickValue = (fieldName) =>
    values[fieldName] ?? resolvedMetadata[fieldName] ?? null;

  const normalized = {
    name: setupSnapshot.setup_name || null,
    source: setupSnapshot.setup_source || null,
    temperature_k: pickValue('temperature_k'),
    viscosity_pa_s: pickValue('viscosity_pa_s'),
    capillary_diam_m: pickValue('capillary_diam_m'),
    eff_cap_length_m: pickValue('eff_cap_length_m'),
  };

  return Object.values(normalized).some((value) => value != null && value !== '')
    ? normalized
    : null;
}

export function getExecutionSetupDetailFromSnapshot(snapshot) {
  if (!isPlainObject(snapshot)) {
    return null;
  }
  return normalizeFrozenSetupSnapshot(snapshot?.setup?.snapshot);
}

export function getRunExecutionSetupDetail(run) {
  return getExecutionSetupDetailFromSnapshot(getRunProvenanceSummary(run));
}

export function getPreviewExecutionSetupDetail(preview) {
  return getExecutionSetupDetailFromSnapshot(preview?.execution_snapshot);
}

export function getRelationshipTypeLabel(value) {
  switch (value) {
    case 'standalone_new_run':
      return 'Standalone new run';
    case 'derived_new_run':
      return 'Derived new run';
    case 'exact_rerun':
      return 'Exact rerun';
    default:
      return value || 'Unknown';
  }
}

export function getRerunReasonLabel(value) {
  switch (value) {
    case 'PARAM_CHANGE':
      return 'Parameter change';
    case 'TRACE_CHANGE':
      return 'Trace selection change';
    case 'CONFIRMATION':
      return 'Confirmation';
    case 'INVESTIGATION':
      return 'Investigation';
    case 'METHOD_UPDATE':
      return 'Method update';
    case 'OTHER':
      return 'Other';
    default:
      return value || '—';
  }
}

function buildTraceMapFromRun(run) {
  return new Map(
    getRunRequestedTraces(run).map((trace) => [
      Number(trace.import_id),
      normalizeTraceSelector(trace),
    ])
  );
}

function buildTraceMapFromSeed(seed) {
  return new Map(
    getDerivedSeedRequestedTraces(seed).map((trace) => [
      Number(trace.import_id),
      normalizeTraceSelector(trace),
    ])
  );
}

export function buildTraceOverridesFromDraft(sourceRun, requestedTraceDraft) {
  const sourceTraceMap = buildTraceMapFromRun(sourceRun);
  const draftItems = Array.isArray(requestedTraceDraft) ? requestedTraceDraft : [];

  return draftItems
    .map((trace) => ({
      import_id: Number(trace.import_id),
      ...normalizeTraceSelector(trace),
    }))
    .filter((trace) => {
      const sourceTrace = sourceTraceMap.get(trace.import_id) || {
        trace_id: null,
        wavelength_nm: null,
      };
      return JSON.stringify(sourceTrace) !== JSON.stringify(normalizeTraceSelector(trace));
    });
}

export function buildTraceOverridesFromSeed(sourceRunSeed, requestedTraceDraft) {
  const sourceTraceMap = buildTraceMapFromSeed(sourceRunSeed);
  const draftItems = Array.isArray(requestedTraceDraft) ? requestedTraceDraft : [];

  return draftItems
    .map((trace) => ({
      import_id: Number(trace.import_id),
      ...normalizeTraceSelector(trace),
    }))
    .filter((trace) => {
      const sourceTrace = sourceTraceMap.get(trace.import_id) || {
        trace_id: null,
        wavelength_nm: null,
      };
      return JSON.stringify(sourceTrace) !== JSON.stringify(normalizeTraceSelector(trace));
    });
}

export function hasParamDraftChanges(sourceRun, paramDraft) {
  const sourceParams = getRunParamsFromProvenance(sourceRun);
  return JSON.stringify(sourceParams) !== JSON.stringify(paramDraft);
}

export function hasRequestedTraceDraftChanges(sourceRun, requestedTraceDraft) {
  return buildTraceOverridesFromDraft(sourceRun, requestedTraceDraft).length > 0;
}

export function getSuggestedRerunReasonCode(sourceRun, paramDraft, requestedTraceDraft) {
  if (hasParamDraftChanges(sourceRun, paramDraft)) {
    return PARAM_CHANGE;
  }
  if (hasRequestedTraceDraftChanges(sourceRun, requestedTraceDraft)) {
    return TRACE_CHANGE;
  }
  return CONFIRMATION;
}

export function hasDerivedDraftChanges(draft) {
  if (!draft?.sourceRunSeed) {
    return false;
  }
  return (
    hasParamDraftChangesForSeed(draft.sourceRunSeed, draft.paramDraft) ||
    hasRequestedTraceDraftChangesForSeed(draft.sourceRunSeed, draft.requestedTraceDraft)
  );
}

export function hasParamDraftChangesForSeed(sourceRunSeed, paramDraft) {
  const sourceParams = getDerivedSeedParams(sourceRunSeed);
  return JSON.stringify(sourceParams) !== JSON.stringify(paramDraft);
}

export function hasRequestedTraceDraftChangesForSeed(sourceRunSeed, requestedTraceDraft) {
  return buildTraceOverridesFromSeed(sourceRunSeed, requestedTraceDraft).length > 0;
}

export function getSuggestedRerunReasonCodeForSeed(
  sourceRunSeed,
  paramDraft,
  requestedTraceDraft
) {
  if (hasParamDraftChangesForSeed(sourceRunSeed, paramDraft)) {
    return PARAM_CHANGE;
  }
  if (hasRequestedTraceDraftChangesForSeed(sourceRunSeed, requestedTraceDraft)) {
    return TRACE_CHANGE;
  }
  return CONFIRMATION;
}

export function seedDerivedDraftFromSeed(sourceRunSeed) {
  const paramDraft = getDerivedSeedParams(sourceRunSeed);
  if (!paramDraft) {
    throw new Error('Saved run is missing frozen params.');
  }

  const requestedTraceDraft = getDerivedSeedRequestedTraces(sourceRunSeed).map((trace) => ({
    import_id: trace.import_id,
    trace_id: trace.trace_id,
    wavelength_nm: trace.wavelength_nm,
  }));

  return {
    sourceRunSeed,
    sourceRunId: getDerivedSeedRunId(sourceRunSeed),
    pipeline: getDerivedSeedPipeline(sourceRunSeed),
    paramDraft,
    requestedTraceDraft,
    previewResponse: null,
    previewStatus: DERIVED_PREVIEW_STATUS.IDLE,
    previewedSignature: null,
    previewError: null,
    saveLoading: false,
    saveError: null,
    rerunReasonCode: getSuggestedRerunReasonCodeForSeed(
      sourceRunSeed,
      paramDraft,
      requestedTraceDraft
    ),
    rerunReasonText: '',
    rerunReasonTouched: false,
  };
}

export function buildDerivedPreviewPayload(draft) {
  const payload = {
    run_id: String(draft.sourceRunId),
  };

  if (draft.pipeline === 'encapsulation') {
    payload.encapsulation_params = cloneJsonValue(draft.paramDraft);
  } else {
    payload.size_params = cloneJsonValue(draft.paramDraft);
  }

  const traceOverrides = buildTraceOverridesFromSeed(
    draft.sourceRunSeed,
    draft.requestedTraceDraft
  );
  if (traceOverrides.length > 0) {
    payload.trace_overrides = traceOverrides;
  }

  return payload;
}

export function getDerivedPreviewSignature(draft) {
  if (!draft?.sourceRunId) {
    return null;
  }
  return JSON.stringify(buildDerivedPreviewPayload(draft));
}

export function getPreviewStatusAfterDerivedDraftChange(draft) {
  if (!draft?.previewResponse || !draft?.previewedSignature) {
    return DERIVED_PREVIEW_STATUS.IDLE;
  }
  return draft.previewedSignature === getDerivedPreviewSignature(draft)
    ? DERIVED_PREVIEW_STATUS.VALID
    : DERIVED_PREVIEW_STATUS.STALE;
}

export function hasFreshDerivedPreview(draft) {
  return (
    Boolean(draft?.previewResponse) &&
    draft?.previewStatus === DERIVED_PREVIEW_STATUS.VALID &&
    draft.previewedSignature === getDerivedPreviewSignature(draft)
  );
}

export function shouldApplyDerivedPreviewResponse(
  draft,
  requestId,
  latestRequestId,
  requestSignature
) {
  if (!draft || requestId !== latestRequestId) {
    return false;
  }
  return getDerivedPreviewSignature(draft) === requestSignature;
}

export function buildDerivedSavePayload(draft, clientRequestId) {
  return {
    ...buildDerivedPreviewPayload(draft),
    client_request_id: clientRequestId,
    rerun_reason_code: draft.rerunReasonCode,
    rerun_reason_text: draft.rerunReasonText,
  };
}

export function canExploreDerivedSeed(seed) {
  if (!getDerivedSeedRunId(seed)) {
    return false;
  }
  if (!['size', 'encapsulation'].includes((getDerivedSeedPipeline(seed) || '').toLowerCase())) {
    return false;
  }
  return Boolean(getDerivedSeedParams(seed)) && getDerivedSeedRequestedTraces(seed).length > 0;
}
